import { parseLineStringSegments } from '@/service/graph'

// Editor layers (VISN-360): read-only overlays that let a parse be checked by eye in editor mode.
//  - flow:        an arrowhead mid-pipe along the pipe's `flows_to` edges;
//  - textLinks:   a line from each label to every pipe or symbol it is linked to, coloured by the rule
//                 that linked it (the flow trace placed it on the pipe it is written along; the
//                 instrument assigner gave the reading to its device; otherwise the nearest object);
//  - lineNumbers: each pipe's line number printed along it.
// Pure geometry here, in image pixels like the rectangles; PnidRenderer draws the result.

export type EditorLayer = 'flow' | 'textLinks' | 'lineNumbers'
export type EditorLayers = Record<EditorLayer, boolean>
export const NO_LAYERS: EditorLayers = { flow: false, textLinks: false, lineNumbers: false }

/** The layers to draw: the Review view hides the parse's links, so the text-link layer stays off there too. */
export const shownLayers = (layers: EditorLayers, hideAssociationLines?: boolean): EditorLayers =>
  hideAssociationLines ? { ...layers, textLinks: false } : layers

export type LinkRule = 'flow_trace' | 'owner' | 'nearest'
export const LINK_RULE_COLORS: Record<LinkRule, string> = {
  flow_trace: '#16a34a',
  owner: '#9333ea',
  nearest: '#6b7280',
}

export interface LayerRect {
  id: string
  x: number
  y: number
  width: number
  height: number
  geometry_wkt?: string
}
export interface LayerEdge {
  fromNodeId: string
  toNodeId: string
  relation?: string
}
export interface LayerNode {
  type?: string
  lineNumber?: string
  writtenAlong?: string
  ownerSymbolId?: string
}

export interface FlowArrow { key: string; x: number; y: number; angle: number }
export interface TextLink {
  key: string
  target: string
  rule: LinkRule
  x1: number
  y1: number
  x2: number
  y2: number
}
export interface LineNumberLabel { key: string; x: number; y: number; angle: number; text: string }

type Pt = { x: number; y: number }

// Edges that link a label to what it names (the parse writes one per label, to its best target).
const LINK_RELATIONS = new Set(['has_label', 'has_annotation', 'annotated_by'])

/** Every part of a pipe's geometry with at least two points (a MULTILINESTRING's parts are not joined). */
const parts = (wkt?: string): Pt[][] =>
  parseLineStringSegments(wkt ?? '')
    .map((part) => part.map(([x, y]) => ({ x, y })))
    .filter((pts) => pts.length >= 2)

/** The longest part of a pipe's geometry: where its flow arrow and line number go. */
const polyline = (wkt?: string): Pt[] => {
  let best: Pt[] = []
  let bestLen = -1
  for (const pts of parts(wkt)) {
    const len = pathLength(pts)
    if (len > bestLen) {
      best = pts
      bestLen = len
    }
  }
  return best
}

const pathLength = (pts: Pt[]): number =>
  pts.slice(1).reduce((sum, p, i) => sum + Math.hypot(p.x - pts[i].x, p.y - pts[i].y), 0)

/** The point halfway along the path and the path's direction there, in degrees. */
const midpoint = (pts: Pt[]): { x: number; y: number; angle: number } => {
  let rest = pathLength(pts) / 2
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    const seg = Math.hypot(b.x - a.x, b.y - a.y)
    if (seg > 0 && (rest <= seg || i === pts.length - 1)) {
      const t = Math.min(1, rest / seg)
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, angle: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI }
    }
    rest -= seg
  }
  return { x: pts[0].x, y: pts[0].y, angle: 0 }
}

const clampToRect = (p: Pt, r: LayerRect): Pt => ({
  x: Math.min(Math.max(p.x, r.x), r.x + r.width),
  y: Math.min(Math.max(p.y, r.y), r.y + r.height),
})

const distToRect = (p: Pt, r: LayerRect): number => {
  const q = clampToRect(p, r)
  return Math.hypot(p.x - q.x, p.y - q.y)
}

const center = (r: LayerRect): Pt => ({ x: r.x + r.width / 2, y: r.y + r.height / 2 })

const closestOnPath = (p: Pt, pts: Pt[]): Pt => {
  let best = pts[0]
  let bestD = Infinity
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len2 = dx * dx + dy * dy
    const t = len2 > 0 ? Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0
    const q = { x: a.x + dx * t, y: a.y + dy * t }
    const d = Math.hypot(p.x - q.x, p.y - q.y)
    if (d < bestD) {
      best = q
      bestD = d
    }
  }
  return best
}

/** The point of any part of the pipe nearest to p: a label beside a short part links to that part. */
const closestOnParts = (p: Pt, pieces: Pt[][]): Pt => {
  let best = pieces[0][0]
  let bestD = Infinity
  for (const pts of pieces) {
    const q = closestOnPath(p, pts)
    const d = Math.hypot(p.x - q.x, p.y - q.y)
    if (d < bestD) {
      best = q
      bestD = d
    }
  }
  return best
}

/** An arrowhead on every pipe the `flows_to` edges direct (symbol → pipe → symbol). */
export const flowArrows = (
  rects: LayerRect[],
  edges: LayerEdge[],
  nodes: Map<string, LayerNode>,
): FlowArrow[] => {
  const byId = new Map(rects.map((r) => [r.id, r]))
  const from = new Map<string, string>() // pipe -> the node flow comes from
  const to = new Map<string, string>() // pipe -> the node flow goes to
  for (const e of edges) {
    if (e.relation !== 'flows_to') continue
    if (nodes.get(e.toNodeId)?.type === 'connection' && !from.has(e.toNodeId)) from.set(e.toNodeId, e.fromNodeId)
    if (nodes.get(e.fromNodeId)?.type === 'connection' && !to.has(e.fromNodeId)) to.set(e.fromNodeId, e.toNodeId)
  }
  const arrows: FlowArrow[] = []
  for (const id of new Set([...from.keys(), ...to.keys()])) {
    const pts = polyline(byId.get(id)?.geometry_wkt)
    if (pts.length < 2) continue
    const first = pts[0]
    const last = pts[pts.length - 1]
    const src = byId.get(from.get(id) ?? '')
    const dst = byId.get(to.get(id) ?? '')
    let forward: boolean
    if (src) forward = distToRect(first, src) <= distToRect(last, src)
    else if (dst) forward = distToRect(last, dst) <= distToRect(first, dst)
    else continue
    arrows.push({ key: id, ...midpoint(forward ? pts : [...pts].reverse()) })
  }
  return arrows
}

/** A line from each label to every pipe or symbol it is linked to, with the rule that linked it. */
export const textLinks = (
  rects: LayerRect[],
  edges: LayerEdge[],
  nodes: Map<string, LayerNode>,
): TextLink[] => {
  const byId = new Map(rects.map((r) => [r.id, r]))
  const targets = new Map<string, Set<string>>() // label -> what it is linked to
  const add = (label: string, target: string) => {
    if (!targets.has(label)) targets.set(label, new Set())
    targets.get(label)!.add(target)
  }
  for (const [id, node] of nodes) {
    if (node.type !== 'tag') continue
    if (node.writtenAlong) add(id, node.writtenAlong)
    if (node.ownerSymbolId) add(id, node.ownerSymbolId)
  }
  for (const e of edges) {
    if (!e.relation || !LINK_RELATIONS.has(e.relation)) continue
    if (nodes.get(e.fromNodeId)?.type === 'tag') add(e.fromNodeId, e.toNodeId)
    else if (nodes.get(e.toNodeId)?.type === 'tag') add(e.toNodeId, e.fromNodeId)
  }
  const links: TextLink[] = []
  for (const [labelId, linked] of targets) {
    const labelRect = byId.get(labelId)
    const label = nodes.get(labelId)
    if (!labelRect || !label) continue
    const c = center(labelRect)
    for (const targetId of linked) {
      const rect = byId.get(targetId)
      const kind = nodes.get(targetId)?.type
      if (!rect || (kind !== 'connection' && kind !== 'symbol')) continue
      let end: Pt
      if (kind === 'connection') {
        const pieces = parts(rect.geometry_wkt)
        if (pieces.length === 0) continue
        end = closestOnParts(c, pieces)
      } else {
        const q = clampToRect(c, rect)
        end = q.x === c.x && q.y === c.y ? center(rect) : q // a label inside its symbol points at the middle
      }
      const rule: LinkRule =
        targetId === label.writtenAlong ? 'flow_trace' : targetId === label.ownerSymbolId ? 'owner' : 'nearest'
      links.push({ key: `${labelId}->${targetId}`, target: targetId, rule, x1: c.x, y1: c.y, x2: end.x, y2: end.y })
    }
  }
  return links
}

/** Each pipe's line number at the middle of the pipe, turned to read along it (bottom to top when vertical). */
export const lineNumberLabels = (rects: LayerRect[], nodes: Map<string, LayerNode>): LineNumberLabel[] => {
  const labels: LineNumberLabel[] = []
  for (const r of rects) {
    const node = nodes.get(r.id)
    if (node?.type !== 'connection' || !node.lineNumber) continue
    const pts = polyline(r.geometry_wkt)
    if (pts.length < 2) continue
    const mid = midpoint(pts)
    let angle = mid.angle
    if (angle >= 90) angle -= 180
    else if (angle < -90) angle += 180
    labels.push({ key: r.id, x: mid.x, y: mid.y, angle, text: node.lineNumber })
  }
  return labels
}
